import "./index.scss";
import * as _ from "lodash";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  editProfile,
  readProfile,
} from "../../../../store/profile/thunkAction";
import { readProfileAppearanceByProfileId } from "../../../../store/profile-appearance/thunkAction";
import { readProfileFormByProfileId } from "../../../../store/profile-form/thunkAction";

import {
  goToChat,
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../utils/event";
import { getImageUrl } from "../../../../utils/image";
import { readProfileRouterByProfileId } from "../../../../store/profile-router/thunkAction";
import { syncProfileSetting } from "../../../../store/profile-setting/thunkAction";

const ProfileDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const profileId = _.get(params, "id");
  const baseURL = window.location.origin;
  const [isChatBot, setIsChatBot] = useState(false);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    dispatch(readProfile(profileId));
    dispatch(readProfileAppearanceByProfileId(profileId));
    dispatch(readProfileFormByProfileId(profileId));
    dispatch(readProfileRouterByProfileId(profileId));
  }, [profileId, dispatch]);

  const profileManage = useSelector((state) => state.profileManage);
  const profile = _.get(profileManage, "profile");

  const profileAppearanceManage = useSelector(
    (state) => state.profileAppearanceManage
  );
  const profileFormManage = useSelector((state) => state.profileFormManage);
  const profileRouterManage = useSelector((state) => state.profileRouterManage);
  const profileSettingManage = useSelector(
    (state) => state.profileSettingManage
  );
  const profileSetting = _.get(profileSettingManage, "profileSetting");

  const currentNoSetting = _.get(profileSetting, "currentNo", 0);
  const quantitySetting = _.get(profileSetting, "quantity", 0);

  const profileAppearance = _.get(profileAppearanceManage, "profileAppearance");
  const profileForm = _.get(profileFormManage, "profileForm");
  const profileRouter = _.get(profileRouterManage, "profileRouter");

  const profileAppearanceId = _.get(profileAppearance, "_id", null);
  const profileFormId = _.get(profileForm, "_id", null);
  const profileRouterId = _.get(profileRouter, "_id", null);

  useEffect(() => {
    setIsChatBot(profile.isChatBot || false);
    setIsActive(profile.isActive || false);
  }, [profile]);

  const onChangeActive = async (checked) => {
    const canChangeActive = checked ? currentNoSetting < quantitySetting : true;

    try {
      if (!canChangeActive) {
        sendNotifyIziMessageError(t("profile.cant_active_profile"));
        return;
      }

      await dispatch(
        editProfile({ id: profileId, data: { isActive: checked } })
      ).unwrap();
    } catch (error) {
      sendNotifyIziMessageError(t("profile.profile_save_error"));
    } finally {
      dispatch(syncProfileSetting());
    }
  };

  const onChangeChatBot = (checked) => {
    dispatch(editProfile({ id: profileId, data: { isChatBot: checked } }));
  };

  const normalizeUrl = (domain) => {
    try {
      const url = new URL(domain);

      return url.href;
    } catch {
      return `https://${domain}`;
    }
  };

  return (
    <div className="container">
      <div className="card bg-white border-0">
        <div className="card-body">
          <div className="row header-title mb-4">
            <div className="col-lg-6">
              <h3 className="color-pri">
                {t("settings.profile.title")} "{profile.domain}"
              </h3>
            </div>
            <div className="col-lg-6 text-start text-lg-end align-self-center">
              <a className="default-link" href="#/izi-message/profile">
                <i className="fa fa-arrow-left"></i>{" "}
                {t("settings.manage.profile_manage")}
              </a>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 mb-4">
              <img src={getImageUrl(profile.imageUrl)} width="90px" alt="" />
            </div>
            <div className="col-lg-7">
              <div className="row">
                <div className="col-sm-4 col-lg-3 mb-3">
                  <button
                    onClick={() => {
                      if (profile.domain) {
                        window.open(normalizeUrl(profile.domain), "_blank");
                      }
                    }}
                    className="btn-live btn btn-success text-white w-100"
                  >
                    <i className="fa fa-globe"></i>{" "}
                    {t("settings.profile.access_website")}
                  </button>
                </div>
                <div className="col-sm-4 col-lg-3 mb-3">
                  <button
                    onClick={() => {
                      goToChat();
                    }}
                    className="btn-live btn btn-primary text-white w-100"
                  >
                    <i className="fa fa-comment-dots"></i>
                    {t("settings.profile.open_imessage")}
                  </button>
                </div>
              </div>
              <div className="small mb-3">
                {t("settings.profile.scrip_decs")}
              </div>
              <button
                onClick={() => {
                  sendNotifyIziMessageSuccess("Copy success");
                  const api = `<script src="${baseURL}/api/script/widget"></script>`;
                  navigator.clipboard.writeText(api);
                }}
                className="btn-copy btn btn-copy-full w-100 justify-content-start text-start"
              >
                &#60;script src="{baseURL}
                /api/script/widget"&#62;&#60;/script&#62;
              </button>
            </div>
          </div>

          <hr className="mb-4 mt-4" />

          <div className="row">
            <div className="col-lg-3">
              <h5 className="title">{t("settings.profile.general")}</h5>
            </div>
            <div className="col-lg-7">
              <ul className="list-group">
                <li className="list-group-item list-group-item-action border-0 mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="mb-1">
                        {t("settings.profile.profile_status")}{" "}
                        {isActive ? (
                          <span className="btn btn-success-light ms-2 btn-sm">
                            {t("common.active")}
                          </span>
                        ) : (
                          <span className="btn btn-danger-light ms-2 btn-sm">
                            {t("common.inactive")}
                          </span>
                        )}
                      </div>
                      <div className="small">
                        {t("settings.profile.profile_status_decs")}
                      </div>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={isActive}
                        onChange={(e) => onChangeActive(e.target.checked)}
                      />
                    </div>
                  </div>
                </li>
                <li className="list-group-item list-group-item-action border-0 mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="mb-1">
                        {t("settings.profile.chat_bot")}{" "}
                        {isChatBot ? (
                          <span className="btn btn-success-light ms-2 btn-sm">
                            {t("common.active")}
                          </span>
                        ) : (
                          <span className="btn btn-danger-light ms-2 btn-sm">
                            {t("common.inactive")}
                          </span>
                        )}
                      </div>
                      <div className="small">
                        {t("settings.profile.chat_bot_desc")}
                      </div>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={isChatBot}
                        onChange={(e) => onChangeChatBot(e.target.checked)}
                      />
                    </div>
                  </div>
                </li>
                <li className="list-group-item list-group-item-action border-0 mb-3">
                  <a
                    href={`#/profile/${profileId}/info`}
                    className="color-dark d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="mb-1">
                        {t("settings.profile.profile_initial_info")}{" "}
                        <span className="small ms-2">
                          <img
                            width="13px"
                            src="/img/chat/checked.png"
                            alt=""
                          />{" "}
                          {t("settings.profile.all_set")}
                        </span>
                      </div>
                      <div className="small">
                        {t("settings.profile.profile_initial_info_desc")}
                      </div>
                    </div>
                    <div>
                      <i className="fa fa-chevron-right"></i>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <hr className="mb-4 mt-4" />

          <div className="row">
            <div className="col-lg-3">
              <h5 className="title">{t("settings.profile.interactions")}</h5>
            </div>
            <div className="col-lg-7">
              <ul className="list-group">
                <li className="list-group-item list-group-item-action border-0 mb-3">
                  <a
                    href={`#/profile/${profileId}/appearances`}
                    className="color-dark d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="mb-1">
                        {t("settings.profile.appearance")}
                        {!profileAppearanceId ? (
                          <span className="small ms-2">
                            <img
                              width="13px"
                              src="/img/chat/false.png"
                              alt=""
                            />{" "}
                            {t("settings.profile.required_setup")}
                          </span>
                        ) : (
                          <span className="small ms-2">
                            <img
                              width="13px"
                              src="/img/chat/checked.png"
                              alt=""
                            />{" "}
                            {t("settings.profile.all_set")}
                          </span>
                        )}
                      </div>
                      <div className="small">
                        {t("settings.profile.appearance_desc")}
                      </div>
                    </div>
                    <div>
                      <i className="fa fa-chevron-right"></i>
                    </div>
                  </a>
                </li>
                <li className="list-group-item list-group-item-action border-0 mb-3">
                  <a
                    href={`#/profile/${profileId}/form`}
                    className="color-dark d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="mb-1">
                        {t("settings.profile.form")}{" "}
                        {!profileFormId ? (
                          <span className="small ms-2">
                            <img
                              width="13px"
                              src="/img/chat/false.png"
                              alt=""
                            />{" "}
                            {t("settings.profile.required_setup")}
                          </span>
                        ) : (
                          <span className="small ms-2">
                            <img
                              width="13px"
                              src="/img/chat/checked.png"
                              alt=""
                            />{" "}
                            {t("settings.profile.all_set")}
                          </span>
                        )}
                      </div>
                      <div className="small">
                        {t("settings.profile.form_desc")}
                      </div>
                    </div>
                    <div>
                      <i className="fa fa-chevron-right"></i>
                    </div>
                  </a>
                </li>
                <li className="list-group-item list-group-item-action border-0 mb-3">
                  <a
                    href={`#/profile/${profileId}/shorthand`}
                    className="color-dark d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="mb-1">
                        {t("settings.profile.shorthand")}
                      </div>
                      <div className="small">
                        {t("settings.profile.shorthand_desc")}
                      </div>
                    </div>
                    <div>
                      <i className="fa fa-chevron-right"></i>
                    </div>
                  </a>
                </li>
                <li className="list-group-item list-group-item-action border-0 mb-3">
                  <a
                    href={`#/profile/${profileId}/trigger`}
                    className="color-dark d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="mb-1">
                        {t("settings.profile.trigger")}
                      </div>
                      <div className="small">
                        {t("settings.profile.trigger_desc")}
                      </div>
                    </div>
                    <div>
                      <i className="fa fa-chevron-right"></i>
                    </div>
                  </a>
                </li>
                <li className="list-group-item list-group-item-action border-0 mb-3">
                  <a
                    href={`#/profile/${profileId}/routing`}
                    className="color-dark d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="mb-1">
                        {t("settings.profile.routing")}
                        {!profileRouterId ? (
                          <span className="small ms-2">
                            <img
                              width="13px"
                              src="/img/chat/false.png"
                              alt=""
                            />{" "}
                            {t("settings.profile.required_setup")}
                          </span>
                        ) : (
                          <span className="small ms-2">
                            <img
                              width="13px"
                              src="/img/chat/checked.png"
                              alt=""
                            />{" "}
                            {t("settings.profile.all_set")}
                          </span>
                        )}
                      </div>
                      <div className="small">
                        {" "}
                        {t("settings.profile.routing_desc")}
                      </div>
                    </div>
                    <div>
                      <i className="fa fa-chevron-right"></i>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetail;
